import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AuthService } from '@auth0/auth0-angular';
import { environment } from 'projects/semeris/src/environments/environment';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

/** Auth0 error codes that can only be resolved by an interactive login/consent flow. */
const INTERACTION_REQUIRED_ERRORS = ['consent_required', 'login_required', 'interaction_required'];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!req.url.includes(environment.apiUrl) && !req.url.includes(environment.graphqlUrl)) {
      return next.handle(req);
    }

    console.debug(`[AuthInterceptor] ${req.url}`);
    const currentUrl = new URL(window.location.href);
    let headers: HttpHeaders;

    if (currentUrl.searchParams.has('token')) {
      console.error(`[AuthInterceptor] Using token from URL parameters`);
      headers = req.headers.set('Authorization', `Bearer ${currentUrl.searchParams.get('token')}`);
      return next.handle(req.clone({ headers }));
    } else {
      return this.authService.getAccessTokenSilently().pipe(
        switchMap((token) => {
          console.debug(`[AuthInterceptor] Token received`);
          headers = req.headers.set('Authorization', `Bearer ${token}`);
          return next.handle(req.clone({ headers }));
        }),
        catchError((error) => {
          if (this.isInteractionRequired(error)) {
            console.warn('[AuthInterceptor] Interactive consent/login required, redirecting to Auth0', error);
            // Silent auth can't resolve consent — send the user through the interactive
            // flow and return them to where they were once consent is granted.
            this.authService
              .loginWithRedirect({
                appState: { target: currentUrl.pathname + currentUrl.search },
              })
              .subscribe();
            // The page is navigating away; abort this request quietly.
            return EMPTY;
          }
          return throwError(() => error);
        })
      );
    }
  }

  private isInteractionRequired(error: unknown): boolean {
    const code = (error as { error?: string } | null)?.error;
    return typeof code === 'string' && INTERACTION_REQUIRED_ERRORS.includes(code);
  }
}
